import classNames from 'classnames';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { ColorKeys, FeatureFlagsEnum, LanguagesAbbr, PageModule, Product, TextOverrides } from '../../../../../../types/working-model';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	rectSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import LargeButton from '../../../../../general-ui/button/large-button';
import { updatePageModule } from '../../../../../../store/actions/admin/create-event/session';
import CreateProductModal from '../../../../content-creation/products';
import Controls from '../../session-module-controls/controls';
import { ProductsLayoutTypesBold, ProductsLayoutTypesClassic, ProductsLayoutTypesFun, TemplateClassNames } from '../../../../../../types/template-layouts';
import './products.scss';
import SortableProduct from '../../../homepage/editor/products/product';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import { getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import { EContentColorOverrideNames } from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import { ETabLayoutClassNames } from '../../../../../live-event/session/session';
import { useParams } from 'react-router';
import { ParamsProps } from '../../../../../live-event/live-event';
import { getStylingOverrides } from '../../../../../live-event/utils';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import { useAppDispatch, useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import DeleteProductConfirmation from '../../../../content-creation/delete-product-confirmation';
import { DeleteChildren } from '../../../../../../connection/products';
import { handleCompleteSelection } from '../../../homepage/editor/products/products-utils';

interface Props {
	page_module: PageModule;
	template: string;
	tabLayout: ETabLayoutClassNames;
}

export default function Products(props: Props): JSX.Element {
	const { page_module, template, tabLayout } = props;
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	const eventId = useTypedSelector(state => state.CreateEventReducer.workingEvent?.event);
	const channelId = useTypedSelector(state => state.CreateEventReducer.workingEvent?.channel);
	const useEventHierarchy = featureFlags[FeatureFlagsEnum.event_hierarchy];

	const dispatch = useAppDispatch();
	const { language } = useParams() as ParamsProps;
	const [modalOpen, setModalOpen] = useState(false);
	const [productToEdit, setProductToEdit] = useState<null | Product>(null);
	const [replacing, setReplacing] = useState<Product | null>(null);
	const content = page_module?.content;
	const styling_overrides = page_module?.styling_overrides;
	const { backgroundColor, backgroundImg } = getStylingOverrides(styling_overrides);
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);
	const [initialSelected, setInitialSelected] = useState(page_module?.modules ?? []);
	const [confirmModal, setConfirmModal] = useState(false);
	const [removing, setRemoving] = useState<Product | undefined>();
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setInitialSelected(page_module?.modules ?? []);
	}, [page_module?.modules]);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8
			}
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);
	// Using theme to pull in layout specific styling from homepage modules
	const layout = useMemo(() => {
		if (template) {
			switch (template) {
				case TemplateClassNames.Apollo: return ProductsLayoutTypesClassic.standard;
				case TemplateClassNames['Studio 54']: return ProductsLayoutTypesBold.grid;
				case TemplateClassNames.O2: return ProductsLayoutTypesFun.vertical_list;
				default: return "standard";
			}
		}
	}, [template]);

	const displayEditModal = useCallback((product: Product) => {
		setProductToEdit(product);
		setModalOpen(true);
	}, []);

	const replaceProduct = useCallback((product: Product) => {
		setProductToEdit(null);
		setReplacing(product);
		setModalOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setModalOpen(false);
		setReplacing(null);
	}, []);

	const openModal = useCallback(() => {
		setReplacing(null);
		setProductToEdit(null);
		setModalOpen(true);
	}, []);

	function handleDragEnd(event: any) {
		if (!page_module.modules) return;
		const _modules = [...page_module.modules];

		const startIndex = _modules.findIndex(
			(product: Product) =>
				Number(product.product) === Number(event.active.id)
		);
		const newIndex = _modules.findIndex(
			(product: Product) =>
				Number(product.product) === Number(event.over.id)
		);

		const [module] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, module);

		dispatch(
			updatePageModule({
				...page_module,
				modules: _modules,
				content_modules: _modules.map(
					(product: Product) => product.product
				),
				is_edited: true,
			})
		);
	}

	function onSelectionComplete(products: Product[]) {
		handleCompleteSelection({
			token,
			channelId,
			eventId,
			page_module,
			setSaving,
			products,
			dispatch,
			isSession: true
		});
	}

	async function removeProduct(productToRemove?: Product) {
		if (!token || !productToRemove?.module_id) return;
		if (page_module.modules) {
			const filteredModules: Product[] = [];
			const filteredContent: number[] = [];

			page_module.modules.forEach((module: Product) => {
				if (module.product && module.product !== productToRemove.product) {
					filteredModules.push(module);
					filteredContent.push(module.product);
				}
			});

			dispatch(
				updatePageModule({
					...page_module,
					modules: filteredModules,
					content_modules: filteredContent,
					is_edited: true,
				})
			);

			// if using event hierarchy AND product contains an event id, meaning it is a child, follow delete child route
			// running this after dispatch, because possible failure on the delete shouldn't prohibit removing the product
			if (useEventHierarchy && productToRemove.event_id) {
				try {
					await DeleteChildren(token, [productToRemove.module_id], productToRemove.event_id);
				} catch (e) {
					console.error(e);
				}
			}
		}
	}

	function confirmRemoveProduct(product: Product) {
		if (useEventHierarchy && product.event_id) {
			setRemoving(product);
			setConfirmModal(true);
		} else {
			removeProduct(product);
		}
	}

	function closeConfirmation() {
		setConfirmModal(false);
		setRemoving(undefined);
	}

	function handleTextChange(key: string) {
		return (text: string) => {
			if (!workingSession) return;
			dispatch(
				updatePageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							baseLanguage: workingSession.default_language,
							language
						}),
					},
					is_edited: true,
				})
			);
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updatePageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}
	const { custom_heading } = getTextValue(content, language);

	if (page_module.is_on && (!workingSession?.layout?.withMenu || page_module)) {
		return (
			<div
				className={classNames(
					`editor-wrapper products`,
					`products-${layout}`,
					`products-${tabLayout}-tabs`,
					backgroundColor,
					template
				)}
				style={backgroundImg}
			>
				<Controls
					page_module={page_module}
					openEditModal={openModal}
				/>
				<div className="container">
					<EditableTextSlim
						tagType="h5"
						className="evt-caption-1 products-custom-heading"
						value={t(`${namespace}:products.${page_module.id}.heading`, t(`${namespace}:products.heading`, custom_heading))}
						defaultColor={ColorKeys.bodyTextColor}
						onChange={handleTextChange('custom_heading')}
						onOverrideChange={handleStyleOverrideChange('custom_heading')}
						elementName={EContentColorOverrideNames.custom_heading}
						colorOverride={styling_overrides?.content?.custom_heading?.color}
					/>

					{page_module.modules && page_module.modules.length > 0 ? (
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							modifiers={[restrictToParentElement]}
							autoScroll={false}
						>
							<SortableContext
								items={page_module.modules.map(
									(module: Product) => String(module.product)
								)}
								strategy={rectSortingStrategy}
							>
								<div className="product-list">
									{page_module.modules.map(
										(module: Product) => (
											<SortableProduct
												product={module}
												key={module.product}
												id={String(module.product)}
												editProduct={() => displayEditModal(module)}
												removeProduct={() => confirmRemoveProduct(module)}
												replaceProduct={() => replaceProduct(module)}
												template={template}
												language={language}
											/>
										)
									)}
								</div>
							</SortableContext>
						</DndContext>
					) : (
						<LargeButton
							title="Add Product"
							style={{ marginTop: 16 }}
							onClick={openModal}
							nonUpload
						/>
					)}
				</div>

				<CreateProductModal
					baseLanguage={workingSession?.default_language as LanguagesAbbr}
					language={language}
					title="Edit products"
					open={modalOpen}
					close={closeModal}
					productToEdit={productToEdit}
					onComplete={onSelectionComplete}
					initialSelected={initialSelected}
					replacing={replacing}
				/>
				<DeleteProductConfirmation
					onConfirm={() => removeProduct(removing)}
					onClose={() => closeConfirmation()}
					open={confirmModal}
					product={removing}
				/>
			</div>
		);
	} else {
		return <div></div>;
	}
}
